import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  useGetArea,
  useGetMeterType,
} from "@features/mx/readCycleManagement/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetAllRoutes, useAddPremises } from "@features/mx/routeManagement/hooks";

interface ReadCycleData {
  id: string;
  routeName: string;
  meterTypeDisplay: string;
  premiseCount: number;
  meterCount: number;
  consumerCount: number;
  createdDate: string;
  createdUserRemoteName: string;
}

interface MoveRoutesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRoutes: string[];
  onMoveRoutes: (destinationCycleId: string) => void;
  estimatedMeters: number;
  selectedPremises: string[];
}

export const MoveRoutesDialog: React.FC<MoveRoutesDialogProps> = ({
  open,
  onOpenChange,
  selectedRoutes,
  onMoveRoutes,
  estimatedMeters,
  selectedPremises,
}) => {
  const {route_id}=useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
  }, [selectedPremises]);

  // extract filters from URL
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentReadType = searchParams.get("readType") || "all";
  const currentArea = searchParams.get("area") || "all";

  const [pendingFilters, setPendingFilters] = useState({
    readType: currentReadType,
    area: currentArea,
  });

  // Data hooks
  const { data: meterTypeData } = useGetMeterType();
  const { data: areaData } = useGetArea();
  const { data: routesData } = useGetAllRoutes(
    currentPage,
    currentSearch,
    currentReadType,
    currentArea
  );

  // destination selection logic
  const [selectedDestination, setSelectedDestination] = useState<string | null>(null);
  const { mutate: addPremises } = useAddPremises(route_id,selectedDestination);

  const handleSelectReadCycle = (id: string) => {
    const selectedRoute = routesData?.results?.find((route) => route.id === id);
    setSelectedDestination(id === selectedDestination ? null : id);
  };

  const handleMoveRoutes = () => {
    if (!selectedDestination) return;

    const payload = {
      premises: selectedPremises,
    };
    addPremises(payload, {
      onSuccess: (res: any) => {
        onMoveRoutes(selectedDestination);
        onOpenChange(false);
      },
      onError: (err: any) => {
      },
    });
  };

  const columns: TableColumn<ReadCycleData>[] = [
    { key: "routeName", header: "Route Name" },
    { key: "meterTypeDisplay", header: "Read Type" },
    { key: "premiseCount", header: "Premises" },
    { key: "meterCount", header: "Meters" },
    { key: "consumerCount", header: "Consumers" },
    { key: "createdDate", header: "Created On" },
    {
      key: "action",
      header: "Action",
      width: "w-24",
      render: (_, row) => (
        <Button
          variant={selectedDestination === row.id ? "default" : "outline"}
          onClick={() => handleSelectReadCycle(row.id)}
        >
          Select
        </Button>
      ),
    },
  ];

  const pagination = routesData
    ? {
        currentPage,
        totalPages: Math.ceil(routesData.count / PAGE_SIZE),
        totalItems: routesData.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, routesData.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const handlePendingFilterChange = (key: keyof typeof pendingFilters, value: string) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      readType: pendingFilters.readType,
      area: pendingFilters.area,
      page: 1,
    };
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    delete newParams.readType;
    delete newParams.area;
    delete newParams.search;
    newParams.page = 1;
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      readType: currentReadType,
      area: currentArea,
    };
    if (searchTerm.trim()) newParams.search = searchTerm.trim();
    else delete newParams.search;
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Read Type</label>
          <Select
            value={pendingFilters.readType}
            onValueChange={(value) => handlePendingFilterChange("readType", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select read type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {meterTypeData?.map((type) => (
                <SelectItem key={type.key} value={String(type.key)}>
                  {type.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Area</label>
          <Select
            value={pendingFilters.area}
            onValueChange={(value) => handlePendingFilterChange("area", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              {areaData?.map((area) => (
                <SelectItem key={area.code} value={String(area.code)}>
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          Clear
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Move selected premises to another route</DialogTitle>
          <DialogDescription>
            Choose a destination route. You can search and filter below.
          </DialogDescription>
        </DialogHeader>

        <GenericTable
          data={routesData?.results ?? []}
          columns={columns}
          rowKey="id"
          emptyMessage="No routes found"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search routes..."
          filterPanel={filterPanel}
        />

        <div className="flex items-center text-sm mt-4">
          <div className="text-gray-700">
            Selected premises:{" "}
            <span className="font-bold">{selectedPremises.length}</span> •
            Estimated meters moved:{" "}
            <span className="font-bold">{estimatedMeters}</span>
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMoveRoutes}
            disabled={!selectedDestination }
            className="bg-blue-600 hover:bg-blue-700"
          >
            Move Routes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
